<template>
  <div class="profile-links" :class="{ 'is-mobile': isMobile }">
    <div class="profile-links-wrapper">
      <template v-for="link in profileLinks" :key="`plink-${link.position}`">
        <gam-nav-item-link v-bind="getLinkItem(link)" />
        <button
          v-if="link.name === GamNavProfileLinks.MASTER && authStore.isAdmin()"
          type="button"
          class="profile-qr"
          :aria-label="localize('master.qr.print')"
          @click="openQr"
        >
          <GamIcon :name="GamIconName.QR_CODE" :size="GamIconSize.SMALL" is-current />
        </button>
      </template>
    </div>
    <div class="additional-actions">
      <gam-button v-bind="getLogout" />
    </div>

    <MastersBook v-if="storyOpen" @close="storyOpen = false" />
    <GamDialog ref="qrDialog">
      <SpotQRCodeDialog
        handshake
        :spot-name="localize('master.qr.title')"
        url="https://gambits.com"
        download-label="master.qr.print"
      />
    </GamDialog>
    <BugReportDialog v-if="showBugReport" @close="showBugReport = false" />
  </div>
</template>

<script setup lang="ts">
import { type UIMyProfileLinkItem } from '@/core/data/config/uiConfig.interface';
import { gambits, localize } from '@/core/gambits';
import NavigationHelper from '@/core/helpers/navigation.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import SpotQRCodeDialog from '@/views/components/gam-dialog/dialog-contents/SpotQRCodeDialog.vue';
import GamDialog from '@/views/components/gam-dialog/GamDialog.vue';
import GamNavItemLink from '@/views/components/gam-nav/GamNavItemLink.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import MastersBook from '@/views/components/MastersBook.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import {
  GamNavProfileLinks,
  GamNavSize,
  GamNavVariant,
} from '@/views/composables/constants/components/gamNav.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamNavItemLinkType } from '@/views/composables/models/components/GamNav';
import BugReportDialog from '@/views/main/walk/modals/BugReportDialog.vue';
import { computed, nextTick, ref } from 'vue';
import { useRouter } from 'vue-router';

const authStore = useAuthStore();
const profileLinks = gambits.configService.getMyProfileLinks();
const router = useRouter();
const storyOpen = ref(false);
const qrDialog = ref<InstanceType<typeof GamDialog>>();
const showBugReport = ref(false);

const openQr = () => {
  qrDialog.value?.show();
};

const getLinkItem = (link: UIMyProfileLinkItem): GamNavItemLinkType => {
  return {
    name: link.name,
    displayName: `links.profile.${link.name}`,
    variant: GamNavVariant.SECONDARY,
    size: GamNavSize.SMALL,
    showArrow: link.showArrow,
    onClick: async () => {
      if (link.name === GamNavProfileLinks.MASTER) {
        storyOpen.value = true;
        return;
      }
      if (link.name === GamNavProfileLinks.BUG) {
        showBugReport.value = true;
        return;
      }
      await NavigationHelper.openLink(router, link);
    },
  };
};

const getLogout = computed((): GamButtonType => {
  return {
    size: GamButtonSize.LARGE,
    variant: GamButtonVariant.TERTIARY,
    label: 'profile.button.logout',
    confirmation: 'profile.button.logoutConfirmation',
    isCentered: true,
    isFullSize: true,
    onClick: async () => {
      await gambits.authService.logout();
      nextTick(() => {
        router.push('/');
      });
    },
  };
});
</script>

<style scoped lang="scss">
.profile-links {
  display: flex;
  padding: 0 var(--spacing-xxl);
  gap: var(--spacing-md);
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  &.is-mobile {
    padding: 0;

    .gam-nav-item-link.secondary.small,
    .profile-qr {
      padding: var(--spacing-xs) 0;
    }
  }

  .profile-qr {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 50px;
    padding: var(--spacing-md) 0;
    border: 0;
    border-bottom: 1px solid var(--color-white-10);
    background: none;
    color: var(--color-white-50);
    cursor: pointer;
  }

  .profile-links-wrapper {
    align-self: stretch;
  }

  .additional-actions {
    margin-top: var(--spacing-md);
    align-self: stretch;
  }
}
</style>
